import { intakeProblemSlugs } from "@/lib/intake-diag";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertStaff(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("assistant")) throw new Error("Forbidden");
  return { isAdmin: roles.includes("admin") };
}

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin")) throw new Error("Forbidden: admin only");
}

/**
 * Derived (never stored) answer to "should this user see the clinic side of
 * the portal?". True for staff, or for anyone who already has a case, a
 * booking, or a session. Self-corrects: the first booking flips it on.
 */
export const hasClinicAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: rolesRow } = await supabase.from("user_roles").select("role").eq("user_id", userId);
    const roles = (rolesRow ?? []).map((r: { role: string }) => r.role);
    if (roles.includes("admin") || roles.includes("assistant")) return { hasClinic: true };

    const { data: prof } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!prof) return { hasClinic: false };

    const [cases, bookings] = await Promise.all([
      supabase.from("cases").select("id").eq("client_id", prof.id).limit(1),
      supabase.from("bookings").select("id").eq("client_id", prof.id).limit(1),
    ]);
    const caseIds = (cases.data ?? []).map((c: { id: string }) => c.id);
    if (caseIds.length || (bookings.data ?? []).length) return { hasClinic: true };

    const { data: sessions } = await supabase
      .from("sessions")
      .select("id")
      .in("case_id", caseIds.length ? caseIds : ["00000000-0000-0000-0000-000000000000"])
      .limit(1);
    return { hasClinic: (sessions ?? []).length > 0 };
  });

/**
 * Phase 1 — Guarantee that a booking is linked to a clinic case.
 *
 * Idempotent: if the booking already has case_id, returns it.
 * Otherwise reuses the client's most recent non-closed case, or opens a new
 * one (`status='active'`, `source='booking'`). Also links the intake
 * form_submission to the same case when present.
 *
 * Internal helper — NOT a server function. Callers must catch and log
 * `clinic.case.link_failed` so payment verification never fails on linkage.
 */
export async function ensureCaseForBooking(
  bookingId: string,
): Promise<{ case_id: string; reused: boolean; created_new: boolean }> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const { data: booking, error: bErr } = await supabaseAdmin
    .from("bookings")
    .select("id, client_id, case_id, intake_submission_id")
    .eq("id", bookingId)
    .maybeSingle();
  if (bErr) throw bErr;
  if (!booking) throw new Error("Booking not found");
  if (!booking.client_id) throw new Error("Booking has no client");

  if (booking.case_id) {
    return { case_id: booking.case_id, reused: true, created_new: false };
  }

  // Find the newest non-closed case for this client.
  const { data: existing } = await supabaseAdmin
    .from("cases")
    .select("id")
    .eq("client_id", booking.client_id)
    .is("deleted_at", null)
    .not("status", "in", "(closed,archived)")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  let caseId: string;
  let createdNew = false;
  if (existing?.id) {
    caseId = existing.id;
  } else {
    const { data: created, error: cErr } = await supabaseAdmin
      .from("cases")
      .insert({
        client_id: booking.client_id,
        child_alias: "—",
        status: "active",
        source: "booking",
        tags: [],
        summary: "",
      })
      .select("id")
      .single();
    if (cErr) throw cErr;
    caseId = created.id;
    createdNew = true;
  }

  const { error: linkErr } = await supabaseAdmin
    .from("bookings")
    .update({ case_id: caseId })
    .eq("id", bookingId)
    .is("case_id", null);
  if (linkErr) throw linkErr;

  if (booking.intake_submission_id) {
    await supabaseAdmin
      .from("form_submissions")
      .update({ case_id: caseId })
      .eq("id", booking.intake_submission_id)
      .is("case_id", null);
  }

  // Phase 3 — lift the parent's chosen main_problem_slug from the intake
  // submission onto the linked case_profile's `presenting_problems.problem_slugs`
  // (dedup append). Only when a profile is already linked to the case; no
  // new profile is created here.
  try {
    if (booking.intake_submission_id) {
      const { data: sub } = await supabaseAdmin
        .from("form_submissions")
        .select("answers")
        .eq("id", booking.intake_submission_id)
        .maybeSingle();
      const subAnswers = (sub?.answers as any) ?? {};
      const slugs = intakeProblemSlugs(subAnswers);
      if (slugs.length > 0) {
        const { data: caseRow } = await supabaseAdmin
          .from("cases")
          .select("case_profile_id")
          .eq("id", caseId)
          .maybeSingle();
        if (caseRow?.case_profile_id) {
          const { data: prof } = await supabaseAdmin
            .from("case_profiles")
            .select("presenting_problems")
            .eq("id", caseRow.case_profile_id)
            .maybeSingle();
          const existing =
            (prof?.presenting_problems as { problem_slugs?: string[] } | null) ?? {};
          const cur: string[] = Array.isArray(existing.problem_slugs) ? existing.problem_slugs : [];
          const added = slugs.filter((s) => !cur.includes(s));
          if (added.length > 0) {
            const next = { ...existing, problem_slugs: [...cur, ...added] };
            await supabaseAdmin
              .from("case_profiles")
              .update({ presenting_problems: next })
              .eq("id", caseRow.case_profile_id);
          }
        }
      }
    }
  } catch {}

  try {
    if (createdNew) {
    }
    // Phase 2 — nudge the parent to open the child file after linkage.
  } catch {}

  return { case_id: caseId, reused: !createdNew, created_new: createdNew };
}

export const retryLinkBookingCase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ bookingId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    try {
      const res = await ensureCaseForBooking(data.bookingId);
      return { ok: true, ...res };
    } catch (e: any) {
      throw e;
    }
  });

// Staff full-row reads must bypass the column-level GRANT restrictions on
// sessions (session_notes, private_notes) and bookings (admin_notes,
// payment_notes). Only call from within a staff-gated handler.
async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

// -------- Cases --------
export const listAdminCases = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const { data: cases, error } = await context.supabase
      .from("cases")
      .select("id, child_alias, child_age_band, status, tags, summary, updated_at, created_at, client_id")
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });
    if (error) throw error;
    const clientIds = Array.from(new Set((cases ?? []).map((c: any) => c.client_id)));
    const { data: profs } = clientIds.length
      ? await context.supabase.from("profiles").select("id, full_name, phone_whatsapp, country").in("id", clientIds)
      : { data: [] };
    const pm = new Map((profs ?? []).map((p: any) => [p.id, p]));
    return (cases ?? []).map((c: any) => ({ ...c, parent: pm.get(c.client_id) ?? null }));
  });

const createCaseInput = z.object({
  child_alias: z.string().min(1),
  child_age_band: z.string().optional().nullable(),
  client_profile_id: z.string().uuid().optional(),
  from_booking_id: z.string().uuid().optional(),
  tags: z.array(z.string()).default([]),
  summary: z.string().optional().default(""),
});

export const createCase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => createCaseInput.parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    let profileId = data.client_profile_id ?? null;
    let bookingId = data.from_booking_id ?? null;

    if (bookingId && !profileId) {
      // bookings.client_id IS the profiles.id — no extra lookup needed.
      const { data: b } = await context.supabase.from("bookings").select("client_id").eq("id", bookingId).maybeSingle();
      if (b?.client_id) profileId = b.client_id;
    }
    if (!profileId) throw new Error("Missing parent profile");

    const { data: row, error } = await context.supabase
      .from("cases")
      .insert({
        client_id: profileId,
        child_alias: data.child_alias,
        child_age_band: data.child_age_band ?? null,
        status: "active",
        tags: data.tags,
        summary: data.summary,
      })
      .select("id")
      .single();
    if (error) throw error;

    // If from booking with a scheduled_at, auto-create a session
    if (bookingId) {
      const { data: b } = await context.supabase
        .from("bookings")
        .select("scheduled_at, meeting_link")
        .eq("id", bookingId)
        .maybeSingle();
      if (b) {
        await context.supabase.from("sessions").insert({
          case_id: row.id,
          booking_id: bookingId,
          scheduled_at: b.scheduled_at,
          meeting_link: b.meeting_link,
          status: "scheduled",
        });
      }
    }

    return { id: row.id };
  });

export const getAdminCase = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const { data: c, error } = await sb
      .from("cases")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    if (!c) return null;
    const [parent, sessions, reports, attempts] = await Promise.all([
      sb.from("profiles").select("id, user_id, full_name, phone_whatsapp, country, city").eq("id", c.client_id).maybeSingle(),
      sb.from("sessions").select("*").eq("case_id", data.id).order("scheduled_at", { ascending: false }),
      sb.from("reports").select("id, title_ar, visibility, shared_at, updated_at, session_id").eq("case_id", data.id).is("deleted_at", null).order("updated_at", { ascending: false }),
      sb.from("attempts").select("id, instrument_id, status, scores, completed_at, created_at").eq("client_id", c.client_id).order("created_at", { ascending: false }),
    ]);
    // Phase 4 — context for the structured session editor.
    // case_profiles is keyed by client_user_id (parent's auth user), not case_id.
    const parentUserId = (parent.data as any)?.user_id ?? null;
    const [profileRes, pillarsRes] = await Promise.all([
      parentUserId
        ? sb
            .from("case_profiles")
            .select("presenting_problems, pillar_screening")
            .eq("client_user_id", parentUserId)
            .maybeSingle()
        : Promise.resolve({ data: null as any }),
      sb.from("pillars").select("slug, name_ar, order").order("order", { ascending: true }),
    ]);
    const pillars = pillarsRes.data ?? [];
    const byPillar: Record<string, { mean: number | null }> =
      (profileRes.data as any)?.pillar_screening?.by_pillar ?? {};
    // Higher mean = more concern (3-point scale where غالبًا=3).
    const screenerTopWeak = Object.entries(byPillar)
      .filter(([, v]) => typeof v?.mean === "number")
      .sort((a, b) => (b[1].mean as number) - (a[1].mean as number))
      .slice(0, 3)
      .map(([slug]) => slug);
    const problemSlugs: string[] = (profileRes.data as any)?.presenting_problems?.problem_slugs ?? [];
    let problemSuggestedPillars: string[] = [];
    if (problemSlugs.length > 0) {
      const { data: cat } = await sb
        .from("problem_catalog")
        .select("slug, label_ar, suggested_pillars")
        .in("slug", problemSlugs);
      const set = new Set<string>();
      (cat ?? []).forEach((r: any) => (r.suggested_pillars ?? []).forEach((p: string) => set.add(p)));
      problemSuggestedPillars = Array.from(set);
    }
    // Phase 1: bookings explicitly linked to this case (bookings.case_id).
    const { data: linkedBookings } = await sb
      .from("bookings")
      .select("id, service_id, scheduled_at, status, payment_status, created_at")
      .eq("case_id", data.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    const svcIds = Array.from(new Set((linkedBookings ?? []).map((b: any) => b.service_id).filter(Boolean)));
    const { data: svcRows } = svcIds.length
      ? await sb.from("services").select("id, title_ar").in("id", svcIds)
      : { data: [] as any[] };
    const svcById = new Map((svcRows ?? []).map((s: any) => [s.id, s]));
    const bookings = (linkedBookings ?? []).map((b: any) => ({
      ...b,
      service: svcById.get(b.service_id) ?? null,
    }));
    // Best-effort: attach the intake submission from the booking that spawned this case
    let intake: any = null;
    try {
      const { data: booking } = await sb
        .from("bookings")
        .select("intake_submission_id")
        .eq("client_id", c.client_id)
        .not("intake_submission_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (booking?.intake_submission_id) {
        const [{ data: sub }, { data: form }] = await Promise.all([
          sb.from("form_submissions").select("id, answers, form_snapshot, created_at").eq("id", booking.intake_submission_id).maybeSingle(),
          sb.from("forms").select("id, title_ar, schema").eq("slug", "consultation-intake").maybeSingle(),
        ]);
        if (sub) intake = { submission: sub, form };
      }
    } catch {}
    return {
      case: c,
      parent: parent.data,
      sessions: sessions.data ?? [],
      reports: reports.data ?? [],
      attempts: attempts.data ?? [],
      intake,
      bookings,
      pillars,
      screener_top_weak: screenerTopWeak,
      problem_suggested_pillars: problemSuggestedPillars,
      presenting_problem_slugs: problemSlugs,
    };
  });

export const updateCase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z.object({
      id: z.string().uuid(),
      status: z.enum(["active","paused","closed"]).optional(),
      tags: z.array(z.string()).optional(),
      summary: z.string().optional(),
      child_alias: z.string().optional(),
      child_age_band: z.string().nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const { id, ...patch } = data;
    const { error } = await context.supabase.from("cases").update(patch).eq("id", id);
    if (error) throw error;
    return { ok: true };
  });

// -------- Sessions --------
// Phase 4 — structured methodology notes.
// `structured_notes` is column-privilege protected (parents cannot read it).
// Staff writes go through supabaseAdmin.
const hypothesisSchema = z.object({
  pillar_slug: z.string().min(1),
  note: z.string().default(""),
});

const structuredNotesSchema = z.object({
  observed_behaviors: z.string().default(""),
  session_observations: z.string().default(""),
  hypotheses: z.array(hypothesisSchema).default([]),
  confirmed_pillars: z.array(z.string()).default([]),
  ruled_out_pillars: z.array(z.string()).default([]),
  plan_directives: z.string().default(""),
  parent_readiness: z.enum(["low", "medium", "high"]).nullable().default(null),
  follow_up_needed: z.boolean().default(false),
  private_notes: z.string().default(""),
}).partial().transform((v) => ({
  observed_behaviors: v.observed_behaviors ?? "",
  session_observations: v.session_observations ?? "",
  hypotheses: v.hypotheses ?? [],
  confirmed_pillars: v.confirmed_pillars ?? [],
  ruled_out_pillars: v.ruled_out_pillars ?? [],
  plan_directives: v.plan_directives ?? "",
  parent_readiness: v.parent_readiness ?? null,
  follow_up_needed: v.follow_up_needed ?? false,
  private_notes: v.private_notes ?? "",
}));

export type StructuredNotes = z.infer<typeof structuredNotesSchema>;

export function completenessOfStructuredNotes(n: Partial<StructuredNotes> | null | undefined): number {
  if (!n) return 0;
  const checks = [
    !!(n.observed_behaviors ?? "").trim(),
    !!(n.session_observations ?? "").trim(),
    (n.hypotheses ?? []).length > 0,
    (n.confirmed_pillars ?? []).length > 0 || (n.ruled_out_pillars ?? []).length > 0,
    !!(n.plan_directives ?? "").trim(),
    !!n.parent_readiness,
  ];
  const hit = checks.filter(Boolean).length;
  return Math.round((hit / checks.length) * 100);
}

export function normalizePillarSelections(n: Partial<StructuredNotes>): Partial<StructuredNotes> {
  const confirmed = Array.from(new Set(n.confirmed_pillars ?? []));
  const ruled = Array.from(new Set((n.ruled_out_pillars ?? []).filter((s) => !confirmed.includes(s))));
  return { ...n, confirmed_pillars: confirmed, ruled_out_pillars: ruled };
}

const upsertSessionInput = z.object({
  id: z.string().uuid().optional(),
  case_id: z.string().uuid(),
  scheduled_at: z.string().nullable().optional(),
  meeting_link: z.string().nullable().optional(),
  status: z.enum(["scheduled","completed","no_show","cancelled"]).default("scheduled"),
  duration_min: z.number().int().min(5).max(240).default(45),
  session_notes: z.string().optional().default(""),
  homework: z.string().optional().default(""),
  next_step: z.string().optional().default(""),
  private_notes: z.string().optional().default(""),
  session_mode: z.enum(["online", "in_person"]).default("online"),
  location_note: z.string().nullable().optional(),
  structured_notes: structuredNotesSchema.optional(),
});

export const upsertSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => upsertSessionInput.parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const { id, ...patch } = data;
    // Validate any pillar slugs against the pillars taxonomy.
    const sn = patch.structured_notes;
    if (sn) {
      const slugsUsed = Array.from(new Set([
        ...sn.hypotheses.map((h) => h.pillar_slug),
        ...sn.confirmed_pillars,
        ...sn.ruled_out_pillars,
      ].filter(Boolean)));
      if (slugsUsed.length > 0) {
        const { data: pillars, error: pErr } = await sb
          .from("pillars")
          .select("slug")
          .in("slug", slugsUsed);
        if (pErr) throw pErr;
        const known = new Set((pillars ?? []).map((p: { slug: string }) => p.slug));
        const unknown = slugsUsed.filter((s) => !known.has(s));
        if (unknown.length > 0) throw new Error(`Unknown pillar slugs: ${unknown.join(", ")}`);
      }
      patch.structured_notes = normalizePillarSelections(sn) as any;
    }
    // Clear the irrelevant location/link when the mode dictates it.
    if (patch.session_mode === "in_person") patch.meeting_link = null;
    if (patch.session_mode === "online") patch.location_note = null;

    if (id) {
      const { error } = await sb.from("sessions").update(patch).eq("id", id);
      if (error) throw error;
      return { id };
    }
    const { data: row, error } = await sb.from("sessions").insert(patch).select("id").single();
    if (error) throw error;
    return { id: row.id };
  });

// -------- Reports --------
const upsertReportInput = z.object({
  id: z.string().uuid().optional(),
  case_id: z.string().uuid(),
  session_id: z.string().uuid().nullable().optional(),
  title_ar: z.string().min(1),
  body_md: z.string().default(""),
  visibility: z.enum(["draft","shared"]).default("draft"),
});

export const upsertReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => upsertReportInput.parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const patch: any = { ...data };
    delete patch.id;
    if (data.visibility === "shared") patch.shared_at = new Date().toISOString();
    if (data.id) {
      const { error } = await context.supabase.from("reports").update(patch).eq("id", data.id);
      if (error) throw error;
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase.from("reports").insert(patch).select("id").single();
    if (error) throw error;
    return { id: row.id };
  });

export const getReport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    // Staff read all fields; client reads only shared-friendly fields.
    const { data: roleRows } = await context.supabase.from("user_roles").select("role").eq("user_id", context.userId);
    const roles = (roleRows ?? []).map((r: any) => r.role);
    const isStaff = roles.includes("admin") || roles.includes("assistant");
    if (isStaff) {
      const sb = await admin();
      const { data: r, error } = await sb.from("reports").select("*").eq("id", data.id).maybeSingle();
      if (error) throw error;
      return r;
    }
    const { data: r, error } = await context.supabase
      .from("reports")
      .select("id, case_id, session_id, title_ar, body_md, parent_report_md, pdf_file, visibility, shared_at, attachments, kind, created_at, updated_at")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    return r;
  });

// -------- Report attachments --------
export const listReportAttachments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { report_id: string }) => z.object({ report_id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: rows, error } = await context.supabase
      .from("report_attachments")
      .select("id, storage_path, file_name, mime_type, size_bytes, created_at")
      .eq("report_id", data.report_id)
      .order("created_at", { ascending: false });
    if (error) throw error;
    const withUrls = await Promise.all((rows ?? []).map(async (r: any) => {
      const { data: signed } = await context.supabase.storage
        .from("reports")
        .createSignedUrl(r.storage_path, 60 * 10);
      return { ...r, url: signed?.signedUrl ?? null };
    }));
    return withUrls;
  });

export const getReportUploadPath = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({ report_id: z.string().uuid(), file_name: z.string().min(1) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const safe = data.file_name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${data.report_id}/${Date.now()}-${safe}`;
    return { path };
  });

export const addReportAttachment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({
      report_id: z.string().uuid(),
      storage_path: z.string().min(1),
      file_name: z.string().min(1),
      mime_type: z.string().optional().nullable(),
      size_bytes: z.number().int().optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("report_attachments")
      .insert({
        report_id: data.report_id,
        storage_path: data.storage_path,
        file_name: data.file_name,
        mime_type: data.mime_type ?? null,
        size_bytes: data.size_bytes ?? null,
        created_by: context.userId,
      })
      .select("id")
      .single();
    if (error) throw error;
    return { id: row.id };
  });

export const removeReportAttachment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row } = await context.supabase.from("report_attachments").select("storage_path, report_id").eq("id", data.id).maybeSingle();
    if (row?.storage_path) {
      await context.supabase.storage.from("reports").remove([row.storage_path]).catch(() => null);
    }
    const { error } = await context.supabase.from("report_attachments").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// -------- Cases: soft-delete (admin only) --------
export const softDeleteCase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("cases").update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// -------- Instruments / Attempts --------
export const listInstruments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    // Staff (admin/assistant) see all statuses so drafts under review are visible;
    // regular signed-in users only see live instruments (RLS also enforces this).
    const { data: roles } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const isStaff = (roles ?? []).some((r: { role: string }) => r.role === "admin" || r.role === "assistant");
    let q = context.supabase
      .from("instruments")
      .select("id, slug, title_ar, subtitle_ar, audience, status, version")
      .order("updated_at", { ascending: false });
    if (!isStaff) q = q.eq("status", "live");
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
  });

export const assignInstrument = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({ case_id: z.string().uuid(), instrument_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const { data: c } = await context.supabase.from("cases").select("client_id").eq("id", data.case_id).maybeSingle();
    if (!c) throw new Error("Case not found");
    const { data: row, error } = await context.supabase
      .from("attempts")
      .insert({
        client_id: c.client_id,
        instrument_id: data.instrument_id,
        status: "in_progress",
        answers: {},
      })
      .select("id")
      .single();
    if (error) throw error;
    return { id: row.id };
  });

export const listMyAttempts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: prof } = await context.supabase.from("profiles").select("id").eq("user_id", context.userId).maybeSingle();
    if (!prof) return [];
    const { data: attempts } = await context.supabase
      .from("attempts")
      .select("id, instrument_id, status, scores, tier, started_at, completed_at, answers")
      .eq("client_id", prof.id)
      .order("started_at", { ascending: false });
    const ids = Array.from(new Set((attempts ?? []).map((a: any) => a.instrument_id)));
    const { data: instruments } = ids.length
      ? await context.supabase.from("instruments").select("id, slug, title_ar, subtitle_ar, config").in("id", ids)
      : { data: [] };
    const im = new Map((instruments ?? []).map((i: any) => [i.id, i]));
    return (attempts ?? []).map((a: any) => ({ ...a, instrument: im.get(a.instrument_id) ?? null }));
  });

export const getAttempt = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: a, error } = await context.supabase
      .from("attempts")
      .select("id, instrument_id, status, answers, scores, started_at, completed_at, client_id")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    if (!a) return null;
    const { data: instrument } = await context.supabase
      .from("instruments")
      .select("id, title_ar, subtitle_ar, description_ar, config")
      .eq("id", a.instrument_id)
      .maybeSingle();
    return { attempt: a, instrument };
  });

function computeScore(instrumentConfig: any, answers: Record<string, any>) {
  const scoring = instrumentConfig?.scoring;
  if (!scoring || scoring.type !== "sum-likert") return { total: 0, band: null };
  const sections = instrumentConfig?.sections ?? [];
  let total = 0;
  for (const s of sections) {
    for (const q of s.questions ?? []) {
      const v = Number(answers[q.id] ?? 0);
      if (!Number.isNaN(v)) total += Math.min(v, scoring.max_per_item ?? 4);
    }
  }
  const bands = scoring.bands ?? [];
  const band = bands.find((b: any) => total >= b.min && total <= b.max)?.label_ar ?? null;
  return { total, band };
}

export const submitAttempt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({ id: z.string().uuid(), answers: z.record(z.string(), z.any()) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const { data: prof } = await context.supabase.from("profiles").select("id").eq("user_id", context.userId).maybeSingle();
    if (!prof) throw new Error("No profile");
    const { data: a } = await context.supabase.from("attempts").select("id, instrument_id, client_id").eq("id", data.id).maybeSingle();
    if (!a || a.client_id !== prof.id) throw new Error("Forbidden");
    const { data: instr } = await context.supabase.from("instruments").select("config").eq("id", a.instrument_id).maybeSingle();
    const score = computeScore(instr?.config, data.answers);
    const now = new Date().toISOString();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("attempts")
      .update({ answers: data.answers, scores: score, status: "scored", completed_at: now })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true, score };
  });

// -------- Portal: cases for parent --------
export const listMyCases = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: prof } = await context.supabase.from("profiles").select("id").eq("user_id", context.userId).maybeSingle();
    if (!prof) return [];
    const { data: cases } = await context.supabase
      .from("cases")
      .select("id, child_alias, child_age_band, status, tags, updated_at")
      .eq("client_id", prof.id)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });
    const caseIds = (cases ?? []).map((c: any) => c.id);
    const [sessionsRes, reportsRes] = caseIds.length
      ? await Promise.all([
          context.supabase.from("sessions").select("id, case_id, scheduled_at, status, homework, next_step, meeting_link, session_mode, location_note, duration_min").in("case_id", caseIds).order("scheduled_at", { ascending: false }),
          context.supabase.from("reports").select("id, case_id, title_ar, shared_at").in("case_id", caseIds).eq("visibility", "shared").is("deleted_at", null),
        ])
      : [{ data: [] }, { data: [] }];
    const sByCase = new Map<string, any[]>();
    (sessionsRes.data ?? []).forEach((s: any) => {
      const arr = sByCase.get(s.case_id) ?? [];
      arr.push(s);
      sByCase.set(s.case_id, arr);
    });
    const rByCase = new Map<string, any[]>();
    (reportsRes.data ?? []).forEach((r: any) => {
      const arr = rByCase.get(r.case_id) ?? [];
      arr.push(r);
      rByCase.set(r.case_id, arr);
    });
    return (cases ?? []).map((c: any) => ({
      ...c,
      sessions: sByCase.get(c.id) ?? [],
      reports: rByCase.get(c.id) ?? [],
    }));
  });

export const listMyReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: prof } = await context.supabase.from("profiles").select("id").eq("user_id", context.userId).maybeSingle();
    if (!prof) return [];
    const { data: cases } = await context.supabase.from("cases").select("id, child_alias").eq("client_id", prof.id).is("deleted_at", null);
    const caseIds = (cases ?? []).map((c: any) => c.id);
    if (!caseIds.length) return [];
    const { data: reports } = await context.supabase
      .from("reports")
      .select("id, case_id, title_ar, body_md, shared_at")
      .in("case_id", caseIds)
      .eq("visibility", "shared")
      .is("deleted_at", null)
      .order("shared_at", { ascending: false });
    const cm = new Map((cases ?? []).map((c: any) => [c.id, c]));
    return (reports ?? []).map((r: any) => ({ ...r, case: cm.get(r.case_id) ?? null }));
  });

export const updateProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({
      full_name: z.string().min(2),
      phone_whatsapp: z
        .string()
        .regex(/^\+[1-9][0-9]{7,14}$/, "الرقم مو مضبوط — اختيار الدولة، وكتابة الرقم بدون الصفر في البداية."),
      country: z.string().min(2),
      city: z.string().optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("profiles")
      .upsert({ user_id: context.userId, ...data }, { onConflict: "user_id" });
    if (error) throw error;
    return { ok: true };
  });